/**
 * Log del cliente.
 *
 * Espejo de `backend/app/core/logging.py`, con el mismo criterio y las mismas
 * tres reglas, adaptadas a un navegador:
 *
 * 1. **Legible en desarrollo.** En la consola, con nivel, mensaje y campos.
 * 2. **Correlacion por peticion.** Todo lo que se manda lleva `request_id`
 *    cuando existe: es el `X-Request-Id` que devuelve el backend, y es lo unico
 *    que permite cruzar lo que vio el navegador con lo que paso en el servidor.
 * 3. **Nunca se registran secretos.** Ni el ID token de Firebase, ni cuerpos de
 *    peticion, ni valores de formulario. Los datos van en campos con nombre, no
 *    interpolados en el mensaje, para poder filtrarlos; y los campos cuyo nombre
 *    ya delata que no deberian estar (token, clave, authorization) no se
 *    registran nunca, ni enmascarados.
 *
 * Dos reglas mas, propias del cliente:
 *
 * - **Un fallo de log jamas puede tumbar la app.** El envio es fire-and-forget:
 *   si falla, no se reintenta ni se loguea el fallo — eso seria un bucle.
 * - **No se inunda la red.** Como maximo `LIMITE_POR_VENTANA` envios por minuto.
 *   Lo que se descarta se cuenta y ese contador viaja con el primer envio de la
 *   ventana siguiente, para que en el log se vea que hubo mas.
 */

/** Ruta del endpoint del backend. Ver el contrato en `POST /logs/client`. */
const RUTA_LOGS = "/logs/client"

/**
 * Base de la API, calculada aparte de `@/lib/api`.
 *
 * A proposito: `api.ts` importa Firebase y ademas importa este modulo. Si el
 * logger dependiera de `api.ts` habria un ciclo, y cualquier prueba del logger
 * arrastraria el SDK de Firebase entero.
 */
const BASE_URL = (
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  "/api"
).replace(/\/$/, "")

export const LIMITE_MENSAJE = 500
export const LIMITE_STACK = 4000
export const LIMITE_CLAVES = 20
export const LIMITE_POR_VENTANA = 10
export const VENTANA_MS = 60_000

export type Nivel = "error" | "warn" | "info" | "debug"

/** El backend habla en tres niveles; `warn` viaja como `warning`. */
const NIVEL_REMOTO: Record<Nivel, string> = {
  error: "error",
  warn: "warning",
  info: "info",
  debug: "info",
}

/** Solo estos dos llegan al backend. El resto se queda en la consola. */
const SE_ENVIAN: Nivel[] = ["error", "warn"]

export type Datos = Record<string, unknown>

/**
 * Lo que jamas debe aparecer en un log, ni por accidente. Misma lista que la del
 * backend: si el patron cambia alla, tiene que cambiar aca.
 */
const SECRETOS =
  /(sk-ant-api\d{2}-[A-Za-z0-9_-]{8,}|sk-(?:proj-)?[A-Za-z0-9_-]{20,}|AIza[0-9A-Za-z_-]{30,}|eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}|(?:postgres(?:ql)?|redis|mysql)(?:\+\w+)?:\/\/[^\s:/]+:[^\s@]+@|-----BEGIN [A-Z ]*PRIVATE KEY-----|(?:clave_sol|usuario_sol|password|passwd|secret|token)["'\s:=]+[^\s,"'}]{4,})/gi

/**
 * Nombres de campo que no se registran, sea cual sea su contenido.
 *
 * A la lista del backend se suman los que en un navegador son la via tipica de
 * filtrar datos del cliente sin querer: el cuerpo de la peticion y los valores
 * de un formulario. Un log que dice "fallo al guardar" sirve; uno que ademas
 * copia el RUC y el importe que se estaban guardando, no.
 */
const CAMPOS_PROHIBIDOS = new Set([
  "clave",
  "claves",
  "sol",
  "password",
  "passwd",
  "secret",
  "secreto",
  "token",
  "jwt",
  "bearer",
  "apikey",
  "authorization",
  "auth",
  "credencial",
  "credenciales",
  "fernet",
  "body",
  "cuerpo",
  "payload",
  "values",
  "valores",
  "form",
  "formulario",
  "campos",
  "fields",
])

/**
 * Se comparan segmentos, no subcadenas: `idToken` es prohibido, `informacion`
 * no. La segunda pasada cubre los nombres pegados sin separador.
 */
function esCampoProhibido(clave: string): boolean {
  const partes = clave
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
  if (partes.some((parte) => CAMPOS_PROHIBIDOS.has(parte))) return true
  return /(password|passwd|token|secret|apikey|api_key|authorization|private_key)/i.test(clave)
}

export function enmascarar(texto: string): string {
  // El patron es global: hay que reiniciarlo antes de cada uso.
  SECRETOS.lastIndex = 0
  return texto.replace(SECRETOS, "«oculto»")
}

function truncar(texto: string, limite: number): string {
  return texto.length <= limite ? texto : `${texto.slice(0, limite - 1)}…`
}

/**
 * Deja los datos en algo que se pueda mandar: plano, corto y sin secretos.
 *
 * Un objeto anidado (una respuesta de la API, el estado de un formulario) se
 * resume a su forma, no a su contenido: lo que interesa del log es que habia un
 * objeto, no que dentro iba el RUC del cliente.
 */
function limpiarDatos(datos?: Datos): Datos | null {
  if (!datos) return null
  const salida: Datos = {}
  let claves = 0
  for (const [clave, valor] of Object.entries(datos)) {
    if (valor === undefined || valor === null) continue
    if (claves >= LIMITE_CLAVES) {
      salida.datos_omitidos = Object.keys(datos).length - claves
      break
    }
    claves += 1
    if (esCampoProhibido(clave)) {
      salida[clave] = "«campo prohibido»"
      continue
    }
    salida[clave] = valorSeguro(valor)
  }
  return Object.keys(salida).length ? salida : null
}

function valorSeguro(valor: unknown): unknown {
  if (typeof valor === "string") return truncar(enmascarar(valor), 300)
  if (typeof valor === "number" || typeof valor === "boolean") return valor
  if (valor instanceof Error) return truncar(enmascarar(`${valor.name}: ${valor.message}`), 300)
  if (Array.isArray(valor)) return `«lista de ${valor.length}»`
  if (typeof valor === "object") return `«objeto de ${Object.keys(valor as object).length} campos»`
  return String(valor)
}

interface CuerpoRemoto {
  nivel: string
  mensaje: string
  ruta: string | null
  stack: string | null
  request_id: string | null
  datos: Datos | null
}

// --- Limitacion de frecuencia -------------------------------------------------
//
// Un error dentro de un render loop dispara cientos de veces por segundo. Sin
// esto, el log del cliente seria un ataque de denegacion contra el propio
// backend.

let ventanaAbierta = 0
let enviadosEnVentana = 0
let descartados = 0

function permitirEnvio(ahora: number): boolean {
  if (ahora - ventanaAbierta >= VENTANA_MS) {
    ventanaAbierta = ahora
    enviadosEnVentana = 0
  }
  if (enviadosEnVentana >= LIMITE_POR_VENTANA) {
    descartados += 1
    return false
  }
  enviadosEnVentana += 1
  return true
}

/** Estado global del limitador. Solo para las pruebas. */
export function reiniciarLimitador(): void {
  ventanaAbierta = 0
  enviadosEnVentana = 0
  descartados = 0
}

// --- Envio --------------------------------------------------------------------

function rutaActual(): string | null {
  if (typeof window === "undefined") return null
  return window.location?.pathname ?? null
}

function enviar(nivel: Nivel, mensaje: string, datos: Datos | null, stack: string | null) {
  if (!SE_ENVIAN.includes(nivel)) return
  if (typeof fetch !== "function") return

  const ahora = Date.now()
  if (!permitirEnvio(ahora)) return

  const finales: Datos = { ...(datos ?? {}) }
  // El `request_id` es campo de primer nivel del contrato, no un metadato mas.
  const requestId = finales.request_id ?? finales.requestId ?? null
  delete finales.request_id
  delete finales.requestId
  if (descartados > 0) {
    finales.envios_descartados = descartados
    descartados = 0
  }

  const cuerpo: CuerpoRemoto = {
    nivel: NIVEL_REMOTO[nivel],
    mensaje: truncar(enmascarar(mensaje), LIMITE_MENSAJE),
    ruta: rutaActual(),
    stack: stack ? truncar(enmascarar(stack), LIMITE_STACK) : null,
    request_id: typeof requestId === "string" ? truncar(requestId, 64) : null,
    datos: Object.keys(finales).length ? finales : null,
  }

  try {
    // `keepalive` para que el ultimo error antes de cerrar la pestana llegue.
    // Ni `await`, ni reintento, ni log del fallo: si el backend no esta, la app
    // tiene que seguir funcionando exactamente igual, y un log del log seria un
    // bucle.
    const promesa = fetch(`${BASE_URL}${RUTA_LOGS}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(cuerpo),
      keepalive: true,
    })
    if (promesa && typeof promesa.catch === "function") promesa.catch(() => {})
  } catch {
    // fetch puede tirar de forma sincrona (CSP, esquema invalido). Silencio.
  }
}

// --- Consola ------------------------------------------------------------------

const COLOR: Record<Nivel, string> = {
  error: "color:#dc2626;font-weight:600",
  warn: "color:#d97706;font-weight:600",
  info: "color:#0891b2;font-weight:600",
  debug: "color:#71717a;font-weight:600",
}

const METODO: Record<Nivel, "error" | "warn" | "info" | "debug"> = {
  error: "error",
  warn: "warn",
  info: "info",
  debug: "debug",
}

function aConsola(nivel: Nivel, mensaje: string, datos: Datos | null) {
  if (!import.meta.env.DEV) return
  const escribir = console[METODO[nivel]] ?? console.log
  const etiqueta = nivel.toUpperCase().padEnd(5)
  if (datos && Object.keys(datos).length) {
    escribir(`%c${etiqueta}%c ${enmascarar(mensaje)}`, COLOR[nivel], "", datos)
  } else {
    escribir(`%c${etiqueta}%c ${enmascarar(mensaje)}`, COLOR[nivel], "")
  }
}

// --- API publica --------------------------------------------------------------

function registrar(nivel: Nivel, mensaje: string, datos?: Datos) {
  const limpios = limpiarDatos(datos)
  // El stack viaja aparte del mensaje: en el contrato es un campo propio.
  const bruto = datos?.stack
  const stack = typeof bruto === "string" ? bruto : null
  if (limpios) delete limpios.stack
  aConsola(nivel, mensaje, limpios)
  enviar(nivel, mensaje, limpios, stack)
}

export const logger = {
  error: (mensaje: string, datos?: Datos) => registrar("error", mensaje, datos),
  warn: (mensaje: string, datos?: Datos) => registrar("warn", mensaje, datos),
  info: (mensaje: string, datos?: Datos) => registrar("info", mensaje, datos),
  debug: (mensaje: string, datos?: Datos) => registrar("debug", mensaje, datos),
}

/**
 * Captura global: lo que nadie atrapo.
 *
 * Un `ErrorBoundary` solo ve lo que revienta durante el render de React. Un
 * error en un handler, en un `setTimeout` o una promesa rechazada sin `catch`
 * no pasa por ahi: hasta ahora moria en la consola de alguien.
 */
export function instalarCapturaGlobal(): void {
  if (typeof window === "undefined") return
  if ((window as unknown as { __logCapturaInstalada?: boolean }).__logCapturaInstalada) return
  ;(window as unknown as { __logCapturaInstalada?: boolean }).__logCapturaInstalada = true

  window.addEventListener("error", (evento: ErrorEvent) => {
    logger.error(evento.message || "error no controlado", {
      origen: "window.error",
      archivo: evento.filename,
      linea: evento.lineno,
      columna: evento.colno,
      stack: evento.error instanceof Error ? evento.error.stack : undefined,
    })
  })

  window.addEventListener("unhandledrejection", (evento: PromiseRejectionEvent) => {
    const motivo: unknown = evento.reason
    const mensaje =
      motivo instanceof Error ? motivo.message : String(motivo ?? "promesa rechazada sin motivo")
    logger.error(mensaje || "promesa rechazada", {
      origen: "unhandledrejection",
      tipo: motivo instanceof Error ? motivo.name : typeof motivo,
      stack: motivo instanceof Error ? motivo.stack : undefined,
    })
  })
}
